import { Component, OnInit } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import {
  FileService,
  UploadProgress,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_LABEL,
} from '../../services/file.service';
import { FileModel } from '../../models/file';

// Config passed via DynamicDialogConfig.data when opening the modal.
export interface FileUploadModalData {
  title?: string; // dialog header
  bucketPrefix: string; // S3 prefix, e.g. 'service-provider-images'
  accept?: string; // input accept filter, e.g. 'image/*' or '.pdf'
  multiple?: boolean; // allow multiple files (default true)
  maxSizeBytes?: number; // per-file cap (default MAX_FILE_SIZE_BYTES)
}

// Result handed back via ref.close(). This is the modal's "@Output" — the caller
// decides what to persist (service media, arrangement doc, etc.). The modal itself
// knows nothing about service providers or arrangements.
export interface FileUploadResult {
  files: FileModel[];
}

interface QueuedFile {
  raw: File;
  name: string;
  type: string;
  size: number;
  percent: number;
  error?: string;
}

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit {
  data!: FileUploadModalData;
  queue: QueuedFile[] = [];
  uploading = false;
  readonly maxSizeLabel = MAX_FILE_SIZE_LABEL;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fileService: FileService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.data = {
      multiple: true,
      accept: '*',
      maxSizeBytes: MAX_FILE_SIZE_BYTES,
      ...(this.config.data as FileUploadModalData),
    };
    if (!this.data.bucketPrefix) {
      // Fail loud rather than silently uploading to the wrong place.
      this.messageService.add({
        severity: 'error',
        summary: 'Upload misconfigured',
        detail: 'No storage location (bucketPrefix) was provided.',
      });
    }
  }

  onSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    const max = this.data.maxSizeBytes ?? MAX_FILE_SIZE_BYTES;
    for (const f of Array.from(input.files)) {
      const error = f.size > max ? `Exceeds ${this.maxSizeLabel}` : undefined;
      this.queue.push({ raw: f, name: f.name, type: f.type, size: f.size, percent: 0, error });
    }
    input.value = ''; // allow re-selecting the same file
  }

  removeAt(i: number): void {
    if (this.uploading) return;
    this.queue.splice(i, 1);
  }

  get canUpload(): boolean {
    return (
      !this.uploading &&
      !!this.data?.bucketPrefix &&
      this.queue.length > 0 &&
      this.queue.every((q) => !q.error)
    );
  }

  upload(): void {
    if (!this.canUpload) return;
    this.uploading = true;

    const onProgress = (p: UploadProgress) => {
      const item = this.queue.find((q) => q.name === p.fileName);
      if (item) item.percent = p.percent;
    };

    this.fileService
      .uploadFilesWithPreSignedUrl(
        {
          prefix: this.data.bucketPrefix,
          uploadData: this.queue.map((q) => ({
            fileName: q.name,
            fileType: q.type,
            fileRawContent: q.raw,
          })),
        },
        onProgress
      )
      .subscribe({
        next: (res: { files: FileModel[] }) => {
          this.uploading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Upload complete',
            detail: `${res.files.length} file(s) uploaded.`,
          });
          // Hand the file records back to the caller. Persistence is the caller's job.
          this.ref.close({ files: res.files } as FileUploadResult);
        },
        error: (err) => {
          this.uploading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Upload failed',
            detail: err?.message ?? 'Unknown error',
          });
        },
      });
  }

  cancel(): void {
    if (this.uploading) return;
    this.ref.close(undefined); // undefined result = cancelled, nothing to persist
  }
}
